import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { payments } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';

const PayPalPaymentButton = ({ amount, orderIds = null, onSuccess, onError }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeAmount, setFeeAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(amount);

  useEffect(() => {
    loadPayPalConfig();
  }, []);

  useEffect(() => {
    if (config) {
      // Calculate fees
      if (config.pass_fees_to_customer) {
        const total = (amount + config.fee_fixed) / (1 - config.fee_percentage / 100);
        setTotalAmount(Math.round(total * 100) / 100);
        setFeeAmount(Math.round((total - amount) * 100) / 100);
      } else {
        setTotalAmount(amount);
        setFeeAmount(0);
      }
    }
  }, [config, amount]);

  const loadPayPalConfig = async () => {
    try {
      const response = await payments.getPayPalConfig();
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load PayPal config:', error);
      toast.error('Payment service unavailable');
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      const originUrl = window.location.origin;
      const response = await payments.createPayPalOrder(originUrl, orderIds);
      return response.data.order_id;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create payment order';
      toast.error(message);
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await payments.capturePayPalOrder(data.orderID);
      
      if (response.data.status === 'completed' || response.data.status === 'already_completed') {
        toast.success(`Payment of $${response.data.amount.toFixed(2)} completed successfully!`);
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        toast.error('Payment was not completed');
        if (onError) {
          onError(new Error('Payment not completed'));
        }
      }
    } catch (error) {
      toast.error('Failed to process payment');
      if (onError) {
        onError(error);
      }
    }
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    toast.error('Payment failed. Please try again.');
    if (onError) {
      onError(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!config || !config.client_id) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        Payment service unavailable
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="text-center py-4 text-amber-600 bg-amber-50 rounded-lg p-4">
        <AlertCircle className="w-6 h-6 mx-auto mb-2" />
        <p className="font-medium">Online payments are currently disabled</p>
        <p className="text-sm text-amber-700">Please contact us for payment options.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Fee breakdown */}
      {feeAmount > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Balance Due:</span>
            <span>${amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-500">
            <span>Processing Fee:</span>
            <span>${feeAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold text-gray-900 border-t pt-2 mt-2">
            <span>Total to Pay:</span>
            <span>${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}
      
      <PayPalScriptProvider options={{ 
        "client-id": config.client_id,
        currency: "AUD",
        intent: "capture"
      }}>
        <PayPalButtons
          style={{ 
            layout: "vertical",
            color: "blue",
            shape: "rect",
            label: "pay",
            height: 45
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onPayPalError}
          onCancel={() => toast.info('Payment cancelled')}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalPaymentButton;
